import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import React, { FC } from 'react';

import { useGetPlayerRgRiskAssessmentOverviewPageLink } from '@/bits/links/useLink';
import { Card, CardBody, CardOptions, CardOptionsButton } from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import { booleanFilter } from '@/components/DataGrid/values';
import { NextIcon, PreviousIcon, RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { assert } from '@/utils/error';
import usePlayerRgAutomationHistory from './usePlayerRgAutomationHistory';

export const Fragment = graphql`
  fragment SanityPlayerRgAutomationHistoryBlock on SanityPlayerRgAutomationHistoryBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const formatEvent = (event: string) => {
  return event
    .split(/(?=[A-Z])/)
    .join(' ')
    .toString();
};

const PlayerRgAutomationHistoryBlock: FC<{
  block: Queries.SanityPlayerRgAutomationHistoryBlockFragment;
}> = ({ block }) => {
  const params = useParams();
  const { t } = useTranslate();

  assert(params.playerId, 'missing playerId');

  const {
    history,
    fetching,
    nextPage,
    previousPage,
    refresh,
    defaultFilter,
    updateFilter,
  } = usePlayerRgAutomationHistory();

  const getRgRiskAssessmentOverviewPage =
    useGetPlayerRgRiskAssessmentOverviewPageLink();

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'playerRgAutomationHistory',
    data: history,
    fetching,
    setOrderBy: () => {},
    desc: true,
    defaultFilter,
    updateFilter,
    getRowLink: ({ row }) =>
      (row?.playerGlobalId &&
        getRgRiskAssessmentOverviewPage(row.playerGlobalId)) ??
      '',
    externalFilters: [
      booleanFilter({
        field: '__showSuspended',
        filterField: 'showSuspended',
        title: 'Show Suspended',
      }),
    ],
    schema: (s) => [
      s.dateTimeValue({
        field: 'processedAt',
        title: 'Processed At',
        fromFilterField: 'from',
        toFilterField: 'to',
      }),
      s.stringValue({
        field: 'eventId',
        title: 'Event Id',
        getValue: ({ row }) => row.eventId,
      }),
      s.stringValue({
        field: 'eventType',
        title: 'Event Type',
        getValue: ({ row }) => formatEvent(row.eventType),
      }),
      s.stringValue({
        field: 'extraInformation',
        title: 'Extra Information',
        getValue: ({ row }) => row.extraInformation ?? '',
      }),
      s.stringValue({
        field: 'riskLevelBefore',
        title: 'Risk Level Before',
        getValue: ({ row }) => row.riskLevelBefore,
      }),
      s.stringValue({
        field: 'riskLevelAfter',
        title: 'Risk Level After',
        getValue: ({ row }) => row.riskLevelAfter,
      }),
      s.stringValue({
        field: 'suspendedBy',
        title: 'Suspended By',
        hidden: true,
        getValue: ({ row }) => row.suspendedBy,
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default PlayerRgAutomationHistoryBlock;
